'use client';

import { getCountryCode, useFormHelper } from '@plura/client/utils';
import { cn } from '@plura/shared/utils';
import { AsYouType, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Icon from '../../atoms/Icon';
import {
  type CountryCode,
  type CountryOption,
  getCountryOptions,
  isSupportedCountry,
} from './countryData';
// Panel visuals are shared with the Dropdown question screen so both pickers
// look identical (overlayPanel / panelSearch / option / mobile overlay).
import '../../organisms/ConversationWrapper/_conversationScreens/DropdownScreen/SearchableDropdown/searchable-dropdown.css';
import './phone-number-input.css';

type PhoneNumberInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'type'
> & {
  id: string;
  error?: string;
  className?: string;
  /** ISO 3166-1 alpha-2 code used before the visitor types anything. */
  defaultCountry?: string;
  /** Form theme shape, matches the option-panel radius to the rest of the form. */
  elementType?: string | null;
};

/** Best-effort E.164 for a partially or fully typed number. */
function toE164(text: string, country: CountryCode): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  const parsed = parsePhoneNumberFromString(trimmed, country);
  if (parsed) return parsed.number;
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  return trimmed.startsWith('+') ? `+${digits}` : `+${getCountryCallingCode(country)}${digits}`;
}

const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  (props, ref) => {
    const { className, defaultCountry, elementType, placeholder, ...rest } = props;
    const { onValueChange, onChange: helperOnChange, errorMessage, value } = useFormHelper(rest);
    const formValue = typeof value === 'string' ? value : '';

    const inputElRef = useRef<HTMLInputElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const lastCommittedRef = useRef<string>(formValue);

    const setInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputElRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [ref],
    );

    const [country, setCountry] = useState<CountryCode>(() => {
      const parsed = formValue ? parsePhoneNumberFromString(formValue) : undefined;
      if (parsed?.country) return parsed.country;
      if (isSupportedCountry(defaultCountry)) return defaultCountry.toUpperCase() as CountryCode;
      return 'US';
    });
    const [display, setDisplay] = useState<string>(() => {
      if (!formValue) return '';
      const parsed = parsePhoneNumberFromString(formValue);
      return parsed?.country ? parsed.formatNational() : formValue;
    });
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [isMobileView, setIsMobileView] = useState(false);

    useLayoutEffect(() => {
      const check = () => setIsMobileView(window.innerWidth < 768);
      check();
      window.addEventListener('resize', check, { passive: true });
      return () => window.removeEventListener('resize', check);
    }, []);

    // Detect the visitor's country after mount (timezone-based) so SSR markup
    // stays deterministic — only when nothing has been typed or configured.
    useEffect(() => {
      if (formValue || defaultCountry) return;
      const detected = getCountryCode();
      if (isSupportedCountry(detected)) setCountry(detected.toUpperCase() as CountryCode);
    }, []);

    // External form changes (reset, resumed conversation) re-derive local state.
    useEffect(() => {
      if (formValue === lastCommittedRef.current) return;
      lastCommittedRef.current = formValue;
      if (!formValue) {
        setDisplay('');
        return;
      }
      const parsed = parsePhoneNumberFromString(formValue);
      if (parsed?.country) {
        setCountry(parsed.country);
        setDisplay(parsed.formatNational());
      } else {
        setDisplay(formValue);
      }
    }, [formValue]);

    const commit = useCallback(
      (text: string, c: CountryCode) => {
        setDisplay(text);
        const e164 = toE164(text, c);
        lastCommittedRef.current = e164;
        // Route through the helper's change handler so an existing validation
        // error re-validates (and clears) as the visitor types.
        helperOnChange({
          currentTarget: { type: 'text', value: e164 },
        } as React.ChangeEvent<HTMLInputElement>);
      },
      [helperOnChange],
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const inputType = (e.nativeEvent as InputEvent).inputType ?? '';
      const isDeletion = inputType.startsWith('delete');
      const atEnd = e.target.selectionStart === raw.length;

      let next = raw;
      let nextCountry = country;

      // Only auto-format when appending at the end — deletions and mid-string
      // edits keep the text as typed so the caret never jumps.
      if (!isDeletion && atEnd) {
        const trimmed = raw.trim();
        if (trimmed.startsWith('+')) {
          const formatter = new AsYouType();
          next = formatter.input(trimmed);
          const detected = formatter.getCountry();
          if (detected) nextCountry = detected;
        } else {
          const digits = raw.replace(/\D/g, '');
          next = digits ? new AsYouType(country).input(digits) : '';
        }
      }

      if (nextCountry !== country) setCountry(nextCountry);
      commit(next, nextCountry);
    };

    const options = useMemo(() => getCountryOptions(), []);
    const filtered = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return options;
      const qDigits = q.replace(/^\+/, '');
      const starts: CountryOption[] = [];
      const contains: CountryOption[] = [];
      for (const option of options) {
        const name = option.name.toLowerCase();
        if (name.startsWith(q) || option.code.toLowerCase() === q || option.callingCode === qDigits) {
          starts.push(option);
        } else if (name.includes(q) || (qDigits && option.callingCode.startsWith(qDigits))) {
          contains.push(option);
        }
      }
      return [...starts, ...contains];
    }, [options, search]);

    const openPanel = useCallback(() => {
      setIsOpen(true);
      setSearch('');
      setFocusedIndex(Math.max(0, options.findIndex((o) => o.code === country)));
      setTimeout(() => searchInputRef.current?.focus(), 30);
    }, [options, country]);

    const closePanel = useCallback(() => {
      setIsOpen(false);
      setSearch('');
      setFocusedIndex(-1);
    }, []);

    const selectCountry = useCallback(
      (option: CountryOption) => {
        const nationalDigits = display.trim().startsWith('+')
          ? parsePhoneNumberFromString(display)?.nationalNumber ?? ''
          : display.replace(/\D/g, '');
        const nextDisplay = nationalDigits
          ? new AsYouType(option.code).input(nationalDigits)
          : '';
        setCountry(option.code);
        commit(nextDisplay, option.code);
        closePanel();
        requestAnimationFrame(() => inputElRef.current?.focus());
      },
      [display, commit, closePanel],
    );

    // Close on outside click.
    useEffect(() => {
      if (!isOpen) return;
      const handler = (e: MouseEvent) => {
        if (!containerRef.current?.contains(e.target as Node)) closePanel();
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [isOpen, closePanel]);

    // Mobile: elevate #conversation-step-wrapper above the logo/badge so the
    // fixed full-screen overlay (inside its stacking context) renders on top.
    useEffect(() => {
      if (!isMobileView || !isOpen) return;
      const wrapper = document.getElementById('conversation-step-wrapper');
      if (!wrapper) return;
      wrapper.style.zIndex = '99999';
      return () => {
        wrapper.style.zIndex = '';
      };
    }, [isOpen, isMobileView]);

    // Inside a MULTI_QUESTION_PAGE, sibling blocks stack in DOM order and
    // would paint over the panel — bump the containing block while open.
    useEffect(() => {
      if (!isOpen) return;
      const wrapper = containerRef.current?.closest('.mqp-inline') as HTMLElement | null;
      if (!wrapper) return;
      const prevPosition = wrapper.style.position;
      const prevZ = wrapper.style.zIndex;
      wrapper.style.position = 'relative';
      wrapper.style.zIndex = '20';
      return () => {
        wrapper.style.position = prevPosition;
        wrapper.style.zIndex = prevZ;
      };
    }, [isOpen]);

    useEffect(() => {
      if (focusedIndex < 0 || !listRef.current) return;
      const el = listRef.current.children[focusedIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }, [focusedIndex]);

    const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        // Opening the picker must not advance the form (global Enter handler).
        e.preventDefault();
        e.stopPropagation();
        if (isOpen) closePanel();
        else openPanel();
      }
    };

    const handlePanelKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((i) => (i < filtered.length - 1 ? i + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((i) => (i > 0 ? i - 1 : filtered.length - 1));
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(filtered.length - 1);
          break;
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          if (focusedIndex >= 0 && filtered[focusedIndex]) selectCountry(filtered[focusedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          closePanel();
          inputElRef.current?.focus();
          break;
        case 'Tab':
          closePanel();
          break;
      }
    };

    const selected = useMemo(
      () => options.find((o) => o.code === country),
      [options, country],
    );

    const panelInner = (
      <>
        <div className="panelSearch">
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setFocusedIndex(0);
            }}
            placeholder="Search countries..."
            autoComplete="off"
            aria-label="Search countries"
            aria-autocomplete="list"
            aria-controls={`${rest.id}-country-listbox`}
            data-gramm="false"
            data-enable-grammarly="false"
          />
          <Icon name="search" className="panelSearchIcon shrink-0 fill-current" />
        </div>
        <div className="panelMenuWrapper">
          <div className="panelMenu">
            <ul
              className="menuList"
              ref={listRef}
              role="listbox"
              id={`${rest.id}-country-listbox`}
              onMouseLeave={() => setFocusedIndex(-1)}
            >
              {filtered.length === 0 ? (
                <li className="menuNotice" role="status">No countries found</li>
              ) : (
                filtered.map((option, index) => (
                  <li
                    key={option.code}
                    role="option"
                    aria-selected={option.code === country}
                    className={cn(
                      'option',
                      'pni-option',
                      index === focusedIndex && 'optionFocused',
                      option.code === country && 'optionSelected',
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectCountry(option);
                    }}
                    onMouseEnter={() => setFocusedIndex(index)}
                  >
                    <span className="pni-option-flag" aria-hidden>{option.flag}</span>
                    <span className="pni-option-name">{option.name}</span>
                    <span className="pni-option-dial">+{option.callingCode}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </>
    );

    return (
      <div ref={containerRef} className="pni-root">
        {/* The screens pass `answerInput`/`empty` here — `empty` carries
            opacity, so it must wrap only the input row. Putting it on the
            root would fade the panel and trap its z-index in a stacking
            context below later fields. */}
        <div className={cn('pni-control', className)}>
          <button
            type="button"
            className="pni-trigger"
            onClick={() => (isOpen ? closePanel() : openPanel())}
            onKeyDown={handleTriggerKeyDown}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-label={selected ? `Country: ${selected.name} +${selected.callingCode}` : 'Select country'}
          >
            <span className="pni-flag" aria-hidden>{selected?.flag}</span>
            <span className="pni-dial">+{selected?.callingCode}</span>
            <Icon name="chevron" className="pni-trigger-chevron shrink-0 fill-current" />
          </button>
          <input
            {...rest}
            ref={setInputRef}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={placeholder ?? '(201) 555-0123'}
            className="pni-input"
            value={display}
            onChange={handleInputChange}
          />
        </div>
        {isOpen && (
          isMobileView ? (
            <div
              className="mobileDropdownOverlay"
              data-dropdown-panel
              onKeyDown={handlePanelKeyDown}
              onClick={(e) => {
                if (e.target === e.currentTarget) closePanel();
              }}
            >
              <div
                className="mobileDropdownClose"
                onClick={closePanel}
                onMouseDown={(e) => e.preventDefault()}
                role="button"
                tabIndex={-1}
                aria-label="Close"
              >
                <Icon name="close" className="!size-5 shrink-0 fill-current" />
              </div>
              <div className={cn('overlayPanel', elementType)}>
                {panelInner}
              </div>
            </div>
          ) : (
            <div className="overlayPanelWrapper pni-overlayBelow">
              <div
                data-dropdown-panel
                className={cn('overlayPanel', elementType)}
                onKeyDown={handlePanelKeyDown}
              >
                {panelInner}
              </div>
            </div>
          )
        )}
        {errorMessage && <p className="pni-error">{errorMessage}</p>}
      </div>
    );
  },
);

PhoneNumberInput.displayName = 'PhoneNumberInput';

export default PhoneNumberInput;
